import { useState } from 'react';

export interface DateValidationResult {
	isValid: boolean;
	errorMessage?: string;
}

export const validateBusinessDate = (dateValue: string | Date | null): DateValidationResult => {
	if (!dateValue) {
		return { isValid: true }; // Allow empty dates, let required validation handle this
	}

	const date = new Date(dateValue);

	// Check if date is valid
	if (isNaN(date.getTime())) {
		return {
			isValid: false,
			errorMessage: 'Invalid date'
		};
	}

	// Check for unreasonable years (catches cases like 10/17/0007)
	const year = date.getFullYear();
	const currentYear = new Date().getFullYear();

	if (year < 2024 || year > currentYear + 2) {
		return {
			isValid: false,
			errorMessage: `Valid values between 2024 and ${currentYear + 2}`
		};
	}

	// Check business rules - not more than 1 year old or 2 years in future
	const today = new Date();

	const yearAgo = new Date(today);
	yearAgo.setFullYear(yearAgo.getFullYear() - 1);

	const twoYearsFromNow = new Date(today);
	twoYearsFromNow.setFullYear(twoYearsFromNow.getFullYear() + 2);

	if (date < yearAgo) {
		return {
			isValid: false,
			errorMessage: 'Date cannot be more than a year old'
		};
	}

	if (date > twoYearsFromNow) {
		return {
			isValid: false,
			errorMessage: 'Date cannot be more than two years in the future'
		};
	}

	return { isValid: true };
};

// Hook for React components to manage date validation state
export const useDateValidation = () => {
	const [dateErrors, setDateErrors] = useState<{ [key: string]: string | null }>({});

	const validateDate = (dateValue: string | Date | null, fieldName: string) => {
		const result = validateBusinessDate(dateValue);
		setDateErrors(prev => ({ ...prev, [fieldName]: result.isValid ? null : result.errorMessage ?? null }));

		return result.isValid;
	};

	const clearDateError = (fieldName: string) => {
		setDateErrors(prev => ({ ...prev, [fieldName]: null }));
	};

	const hasDateErrors = Object.values(dateErrors).some(error => error !== null);

	return {
		dateErrors,
		hasDateErrors,
		validateDate,
		clearDateError
	};
};
